/** importing libraries **/
import {
    createSlice,
    createAsyncThunk,
    createSelector,
    PayloadAction,
    SerializedError,
} from "@reduxjs/toolkit";

/** importing dependencies **/
import { RootState } from "../store";
import { IIdentityCapture } from "../../constants/identityCapture";
import type { OrgBranding } from "../../utils/orgBranding";
import { organizationApi } from "../../services/apis/organization.api";
import { auditApi } from "../../services/apis/audit.api";
import {
    minimalToast,
    NotificationCategory,
    NotificationPriority,
    NotificationSeverity,
} from "../../utils/toast";
import AuthUtils from "../../utils/auth.utils";
import { schoolFlowApi } from "../../services/apis/schoolFlow.api";
import {
    getMasterOrgIdForChildren,
    mergeOrganizationsWithSubOrgs,
} from "../../utils/orgHierarchy";
import type { StudentTaskBoardConfig } from "../../constants/studentTaskBoard";
import type { ParentReportConfig } from "../../constants/parentReport";

interface List {
    _id: string;
    name: string;
    organization: string;
    members: {
        user: any;
        role: string;
        _id: string;
    }[];
    createdAt?: Date;
    updatedAt?: Date;
    __v?: number;
}

interface SubscriptionPlan {
    billingCycle: 'monthly' | 'annual';
    freeTrialEndsAt?: Date;
    slots?: {
        auditeeCount: number;
        adminCount: number;
        superAdminCount: number;
        auditorCount: number;
        organizationCount: number;
    };
    invites?: {
        auditee: number;
        auditor: number;
        evaluator: number;
    };
    subscriptionEndDate?: Date;
    aiCredits?: {
        totalCoins: number;
        usedCoins: number;
        costPerCoin: number; // in INR
        creditAddedAt?: Date;
        expiresAt?: Date;
    };
}

interface Organization {
    _id: string;
    name: string;
    auditCategories: AuditCategory[];
    licenseId?: string;
    logo?: string;
    logoBase64?: string;
    subscriptionPlan?: SubscriptionPlan;
    pactSettings?: {
        internalWeight: number;
        externalWeight: number;
        peerWeight: number;
        selfWeight?: number;
    };
    features?: {
        imageVerification?: boolean;
        aiAudits?: boolean;
        assessments?: boolean;
        oneTimeAudits?: boolean;
        anecdotalRecords?: boolean;
    };
    // Per-organization customization flags. Extensible { featureName: boolean } map
    // so some orgs can opt into customizations others don't have. Known keys:
    // `customAssessmentInstruction` (show org-specific assessment instruction text).
    // `imageQuestionSplitLayout` (image left, options right, palette bottom for image questions).
    // `evaluationReportTermsFirst` (order the individual evaluation report PDF as
    // Part 1 Baseline, Part 2 Endline, Part 3 Changes instead of changes-first; used by Rotary).
    // `evaluationReportHideInterdependenceAndFlags` (omit the Parameter Interdependence and
    // Notable Flags pages from the individual evaluation report PDF; used by Rotary).
    // `identityCapture` is a structured nested object (not a boolean): it configures which
    // identity fields a participant enters when opening an assessment/evaluation.
    // `branding` is a structured, vendor-agnostic object (logo/colors/tagline) that themes
    // participant screens and report PDFs. See utils/orgBranding.ts (OrgBranding).
    customization?: {
        customAssessmentInstruction?: boolean;
        imageQuestionSplitLayout?: boolean;
        evaluationReportTermsFirst?: boolean;
        evaluationReportHideInterdependenceAndFlags?: boolean;
        identityCapture?: IIdentityCapture;
        branding?: OrgBranding;
        studentTaskBoard?: StudentTaskBoardConfig;
        // `parentReport` switches the individual evaluation report to the
        // parent-facing variant. See constants/parentReport.ts.
        parentReport?: ParentReportConfig;
        [key: string]:
            | boolean
            | IIdentityCapture
            | OrgBranding
            | StudentTaskBoardConfig
            | ParentReportConfig
            | undefined;
    };
    // School-flow hierarchy flags. Surface conditional UI (sidebar entry,
    // "Add Sub-Org / School", rollup dashboard) when isMasterOrganization=true.
    isMasterOrganization?: boolean;
    parentOrganizationId?: string;
    schoolProfile?: {
        schoolId: string;
        category: string;
        medium: string;
        stateCode: string;
        districtCode: string;
        sequence: number;
        status: 'active' | 'inactive' | 'archived';
    };
    classes?: { name: string; sections: string[] }[];
    /** ISO 639-1 codes enabled for assessment content (e.g. en, kn). */
    assessmentLanguages?: string[];
}

interface AuditForm {
    _id: string;
    title: string;
}

interface AuditCategory {
    _id: string;
    name: string;
    auditForms: AuditForm[];
    lists: List[];
    parameters: string[];
    organizations: string[];
    createdAt?: Date;
    updatedAt?: Date;
}

interface OrganizationsState {
    allOrganizations: Organization[];
    organizationsOfAdmin: Organization[];
    /** Sub-orgs loaded lazily for the active master org (navbar + shared pickers). */
    subOrganizations: Organization[];
    loadedSubOrgsForMasterId: string | null;
    selectedOrganization: Organization | null;
    auditCategories: AuditCategory[];
    auditForms: AuditForm[];
    isLoading: boolean;
    error: string | null;
    selectedAuditCategory: AuditCategory | null;
}

const initialState: OrganizationsState = {
    allOrganizations: [],
    organizationsOfAdmin: [],
    subOrganizations: [],
    loadedSubOrgsForMasterId: null,
    selectedOrganization: null,
    auditCategories: [],
    auditForms: [],
    isLoading: false,
    error: null,
    selectedAuditCategory: null,
};

/**
 * @description - To register a new Organization
 */
export const createOrganization = createAsyncThunk(
    "organization/create",
    async (details: any, { getState }) => {
        try {
            const { access_token } = (getState() as RootState).auth;
            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const { data } = await organizationApi.create(details, header);
            return data;
        } catch (err: any) {
            console.log(err);
        }
    }
);

interface FetchOrgListOptions {
    mastersOnly?: boolean;
    minimal?: boolean;
    force?: boolean;
}

/**
 * @description - To fetching all organizations
 */
export const fetchAllOrganizations = createAsyncThunk(
    "organizations/fetchAllOrganizations",
    async (
        options: FetchOrgListOptions = {},
        { rejectWithValue, getState }
    ) => {
        try {
            const state = (getState() as RootState).organization;
            const minimal = options.minimal !== false;
            const mastersOnly = options.mastersOnly !== false;
            if (
                !options.force &&
                minimal &&
                mastersOnly &&
                state.allOrganizations.length > 0
            ) {
                return state.allOrganizations;
            }

            const { access_token } = (getState() as RootState).auth;
            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const response = await organizationApi.getAll(header, options);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch organizations for admin"
            );
        }
    }
);

export type FetchSubOrganizationsArg =
    | string
    | { masterOrgId: string; force?: boolean };

/**
 * @description - Fetch sub-organizations for a master org (lazy load after master selection)
 */
export const fetchSubOrganizations = createAsyncThunk(
    "organizations/fetchSubOrganizations",
    async (
        arg: FetchSubOrganizationsArg,
        { rejectWithValue, getState }
    ) => {
        const masterOrgId = typeof arg === "string" ? arg : arg.masterOrgId;
        const force = typeof arg === "string" ? false : Boolean(arg.force);
        try {
            const state = (getState() as RootState).organization;
            if (!force && state.loadedSubOrgsForMasterId === masterOrgId) {
                return {
                    masterOrgId,
                    subOrganizations: state.subOrganizations,
                };
            }

            const { access_token } = (getState() as RootState).auth;
            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const response = await schoolFlowApi.listChildren(
                masterOrgId,
                header,
                undefined,
                { picker: true }
            );
            return {
                masterOrgId,
                subOrganizations: (response?.data || []) as Organization[],
            };
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch sub-organizations"
            );
        }
    }
);

/**
 * @description - To fetching organizations of an admin
 */
export const fetchOrganizationsOfAdmin = createAsyncThunk(
    "organizations/fetchOrganizationsOfAdmin",
    async (
        options: FetchOrgListOptions = {},
        { rejectWithValue, getState }
    ) => {
        try {
            const state = (getState() as RootState).organization;
            const minimal = options.minimal !== false;
            const mastersOnly = options.mastersOnly !== false;
            if (
                !options.force &&
                minimal &&
                mastersOnly &&
                state.organizationsOfAdmin.length > 0
            ) {
                return state.organizationsOfAdmin;
            }

            const { access_token } = (getState() as RootState).auth;
            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const response = await organizationApi.getAllOrganizationAdmin(
                header,
                options
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch organizations for admin"
            );
        }
    }
);

/**
 * @description - To fetching audit categories for a selected organization
 */
export const fetchAuditCategoriesByOrganizationId = createAsyncThunk(
    "organizations/fetchAuditCategories",
    async (organizationId: string, { rejectWithValue, getState }) => {
        try {
            const { access_token } = (getState() as RootState).auth;
            // Use AuthUtils.getHeaders to include invitation token if anonymous
            const header = AuthUtils.getHeaders(access_token);
            const response = await auditApi.getAuditCategoryByOrganizationId(
                organizationId,
                { noPopulate: true },
                header
            );
            console.log("API Response:", response); // Debug log
            return response.data;
        } catch (error: any) {
            console.error("API Error:", error); // Error log
            return rejectWithValue(
                error.message || "Failed to fetch audit categories"
            );
        }
    }
);

/**
 * @description - To fetching a single organization by ID
 */
export const fetchOrganizationById = createAsyncThunk(
    "organizations/fetchOrganizationById",
    async (organizationId: string, { rejectWithValue, getState }) => {
        try {
            const { access_token } = (getState() as RootState).auth;
            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const response = await organizationApi.getById(
                organizationId,
                header
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch organization"
            );
        }
    }
);

/**
 * @description - Assign an audit category to specific or multiple organizations
 */
export const assignAuditCategoryToOrganizations = createAsyncThunk(
    "organizations/assignAuditCategoryToOrganizations",
    async (
        {
            auditCategoryId,
            organizationIds,
        }: { auditCategoryId: string; organizationIds: string[] },
        { rejectWithValue, getState }
    ) => {
        try {
            const { access_token } = (getState() as RootState).auth;
            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const body: any = {
                auditCategoryId,
                targetOrganizationIds: organizationIds,
            };
            const response = await auditApi.assignAuditCategoryToOrganization(
                body,
                header
            );
            return response;
        } catch (error: any) {
            return rejectWithValue(
                error.message ||
                "Failed to assign audit category to organizations"
            );
        }
    }
);

// Add a new async thunk for renewing the plan
export const renewPlan = createAsyncThunk(
    "organization/renewPlan",
    async (details: any, { getState, rejectWithValue, dispatch }) => {
        try {
            const { access_token } = (getState() as RootState).auth;
            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const { data } = await organizationApi.renewPlan(details.orgId, details, header);
            // After successful renewal, fetch the latest organization data
            if (data && data.orgId) {
                await dispatch(fetchOrganizationById(data.orgId));
            }
            return data;
        } catch (err: any) {
            return rejectWithValue(err.message || "Failed to renew plan");
        }
    }
);

/**
 * @description - To delete an organization (P3MSuperAdmin only)
 */
export const deleteOrganization = createAsyncThunk(
    "organization/delete",
    async (organizationId: string, { getState, rejectWithValue, dispatch }) => {
        try {
            const { access_token } = (getState() as RootState).auth;
            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const { data } = await organizationApi.delete(organizationId, header);
            // After successful deletion, refetch organizations
            await dispatch(fetchAllOrganizations({}));
            return { organizationId, data };
        } catch (err: any) {
            return rejectWithValue(err.message || "Failed to delete organization");
        }
    }
);

const organizationsSlice = createSlice({
    name: "organizations",
    initialState,
    reducers: {
        setSelectedOrganization: (state, action: PayloadAction<Organization | null>) => {
            state.selectedOrganization = action.payload;
        },
        setSelectedAuditCategory: (
            state,
            action: PayloadAction<AuditCategory | null>
        ) => {
            state.selectedAuditCategory = action.payload;
        },
        updateAuditCategoryLists: (
            state,
            action: PayloadAction<{ categoryId: string; lists: List[] }>
        ) => {
            const { categoryId, lists } = action.payload;
            const categoryIndex = state.auditCategories.findIndex(cat => cat._id === categoryId);
            if (categoryIndex !== -1) {
                state.auditCategories = state.auditCategories.map((category, index) => {
                    if (index === categoryIndex) {
                        return {
                            ...category,
                            lists: lists.map(list => ({
                                _id: list._id,
                                name: list.name,
                                organization: list.organization,
                                members: list.members.map(member => ({
                                    _id: member._id,
                                    user: member.user,
                                    role: member.role
                                })),
                                createdAt: list.createdAt,
                                updatedAt: list.updatedAt,
                                __v: list.__v
                            }))
                        };
                    }
                    return category;
                });
            }
        },
        updateAICredits: (
            state,
            action: PayloadAction<{ organizationId: string; aiCredits: SubscriptionPlan['aiCredits'] }>
        ) => {
            const { organizationId, aiCredits } = action.payload;

            // Update selectedOrganization if it matches
            if (state.selectedOrganization && state.selectedOrganization._id === organizationId) {
                state.selectedOrganization = {
                    ...state.selectedOrganization,
                    subscriptionPlan: {
                        ...state.selectedOrganization.subscriptionPlan,
                        aiCredits: aiCredits ? { ...aiCredits } : state.selectedOrganization.subscriptionPlan?.aiCredits,
                    } as SubscriptionPlan,
                };
            }

            // Update in organizations lists
            const updateOrgArray = (arr: Organization[]) =>
                arr.map((org) =>
                    org._id === organizationId
                        ? {
                            ...org,
                            subscriptionPlan: {
                                ...org.subscriptionPlan,
                                aiCredits: aiCredits ? { ...aiCredits } : org.subscriptionPlan?.aiCredits,
                            } as SubscriptionPlan,
                        }
                        : org
                );

            state.allOrganizations = updateOrgArray(state.allOrganizations);
            state.organizationsOfAdmin = updateOrgArray(state.organizationsOfAdmin);
        },
        updateOrganizationDetails: (
            state,
            action: PayloadAction<{ organizationId: string; name?: string; location?: string; description?: string; logo?: string }>
        ) => {
            const { organizationId, name, location, description, logo } = action.payload;

            // Helper to update org fields
            const updateOrg = (org: any) => ({
                ...org,
                ...(name !== undefined && { name }),
                ...(location !== undefined && { location }),
                ...(description !== undefined && { description }),
                ...(logo !== undefined && { logo }),
            });

            // Update selectedOrganization if it matches
            if (state.selectedOrganization && state.selectedOrganization._id === organizationId) {
                state.selectedOrganization = updateOrg(state.selectedOrganization);
            }

            // Update in allOrganizations
            state.allOrganizations = state.allOrganizations.map((org) =>
                org._id === organizationId ? updateOrg(org) : org
            );

            // Update in organizationsOfAdmin
            state.organizationsOfAdmin = state.organizationsOfAdmin.map((org) =>
                org._id === organizationId ? updateOrg(org) : org
            );
        },
        updateOrganizationCustomization: (
            state,
            action: PayloadAction<{ organizationId: string; customization: Organization['customization'] }>
        ) => {
            const { organizationId, customization } = action.payload;

            // Merge incoming flags onto whatever the org already has
            const mergeOrg = (org: Organization): Organization => ({
                ...org,
                customization: { ...(org.customization || {}), ...(customization || {}) },
            });

            if (state.selectedOrganization && state.selectedOrganization._id === organizationId) {
                state.selectedOrganization = mergeOrg(state.selectedOrganization);
            }

            state.allOrganizations = state.allOrganizations.map((org) =>
                org._id === organizationId ? mergeOrg(org) : org
            );

            state.organizationsOfAdmin = state.organizationsOfAdmin.map((org) =>
                org._id === organizationId ? mergeOrg(org) : org
            );
        },
        clearSubOrganizations: (state) => {
            state.subOrganizations = [];
            state.loadedSubOrgsForMasterId = null;
        },
    },
    extraReducers: (builder) => {
        // Handle createOrganization
        builder.addCase(createOrganization.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(
            createOrganization.fulfilled,
            (state, action: PayloadAction<Organization>) => {
                if (action.payload) {
                    state.allOrganizations.push(action.payload);
                    state.organizationsOfAdmin.push(action.payload);
                }
            }
        );
        builder.addCase(
            createOrganization.rejected,
            (
                state,
                action: PayloadAction<
                    SerializedError,
                    string,
                    { rejectedWithValue: boolean }
                >
            ) => {
                state.isLoading = false;
                if (action.payload && action.meta.rejectedWithValue) {
                    state.error = action.payload.message;
                } else {
                    state.error = "An error occurred";
                }
            }
        );
        // Handle fetchAllOrganizations
        builder.addCase(fetchAllOrganizations.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(
            fetchAllOrganizations.fulfilled,
            (state, action: PayloadAction<Organization[]>) => {
                state.isLoading = false;
                state.allOrganizations = action.payload;
            }
        );
        builder.addCase(
            fetchAllOrganizations.rejected,
            (
                state,
                action: PayloadAction<
                    SerializedError,
                    string,
                    { rejectedWithValue: boolean }
                >
            ) => {
                state.isLoading = false;
                if (action.payload && action.meta.rejectedWithValue) {
                    state.error = action.payload.message;
                } else {
                    state.error = "An error occurred";
                }
            }
        );

        // Handle fetchOrganizationsOfAdmin
        builder.addCase(fetchOrganizationsOfAdmin.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(
            fetchOrganizationsOfAdmin.fulfilled,
            (state, action: PayloadAction<Organization[]>) => {
                state.isLoading = false;
                state.organizationsOfAdmin = action.payload;
                state.selectedOrganization =
                    action.payload.length > 0 ? action.payload[0] : null;
            }
        );
        builder.addCase(
            fetchOrganizationsOfAdmin.rejected,
            (
                state,
                action: PayloadAction<
                    SerializedError,
                    string,
                    { rejectedWithValue: boolean }
                >
            ) => {
                state.isLoading = false;
                if (action.payload && action.meta.rejectedWithValue) {
                    state.error = action.payload.message;
                } else {
                    state.error = "An error occurred";
                }
            }
        );

        builder.addCase(fetchSubOrganizations.pending, (state) => {
            state.error = null;
        });
        builder.addCase(
            fetchSubOrganizations.fulfilled,
            (
                state,
                action: PayloadAction<{
                    masterOrgId: string;
                    subOrganizations: Organization[];
                }>
            ) => {
                state.isLoading = false;
                state.loadedSubOrgsForMasterId = action.payload.masterOrgId;
                state.subOrganizations = action.payload.subOrganizations;
            }
        );
        builder.addCase(fetchSubOrganizations.rejected, (state) => {
            state.subOrganizations = [];
            state.loadedSubOrgsForMasterId = null;
        });

        // Handle fetchAuditCategories
        builder.addCase(
            fetchAuditCategoriesByOrganizationId.pending,
            (state) => {
                state.isLoading = true;
                state.error = null;
            }
        );
        builder.addCase(
            fetchAuditCategoriesByOrganizationId.fulfilled,
            (state, action: PayloadAction<AuditCategory[]>) => {
                console.log("Updating state with:", action.payload); // Debug log
                state.isLoading = false;
                state.auditCategories = action.payload.map(category => ({
                    ...category,
                    lists: category.lists || [], // Ensure lists is always an array
                }));
                state.auditForms = action.payload.flatMap((category) => category.auditForms);
            }
        );
        builder.addCase(
            fetchAuditCategoriesByOrganizationId.rejected,
            (
                state,
                action: PayloadAction<
                    SerializedError,
                    string,
                    { rejectedWithValue: boolean }
                >
            ) => {
                state.isLoading = false;
                if (action.payload && action.meta.rejectedWithValue) {
                    state.error = action.payload.message;
                } else {
                    state.error = "An error occurred";
                }
            }
        );

        // Handle fetchOrganizationById
        builder.addCase(fetchOrganizationById.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(
            fetchOrganizationById.fulfilled,
            (state, action: PayloadAction<Organization>) => {
                state.isLoading = false;
                state.selectedOrganization = action.payload;
            }
        );
        builder.addCase(
            fetchOrganizationById.rejected,
            (
                state,
                action: PayloadAction<
                    SerializedError,
                    string,
                    { rejectedWithValue: boolean }
                >
            ) => {
                state.isLoading = false;
                if (action.payload && action.meta.rejectedWithValue) {
                    state.error = action.payload.message;
                } else {
                    state.error = "An error occurred";
                }
            }
        );

        // Handle assignAuditCategoryToOrganizations
        builder.addCase(assignAuditCategoryToOrganizations.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(
            assignAuditCategoryToOrganizations.fulfilled,
            (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                // Optionally update state if needed
                state.auditCategories = [
                    ...state.auditCategories,
                    action.payload,
                ];
                minimalToast({
                    title: "Success",
                    message:
                        "Audit category assigned successfully to organizations.",
                    type: NotificationSeverity.SUCCESS,
                    category: NotificationCategory.AUDIT,
                    priority: NotificationPriority.MEDIUM,
                });
            }
        );
        builder.addCase(
            assignAuditCategoryToOrganizations.rejected,
            (state, action) => {
                state.isLoading = false;
                state.error = (action.payload as string) || "An error occurred";
                minimalToast({
                    title: "Error",
                    message: state.error,
                    type: NotificationSeverity.ERROR,
                    category: NotificationCategory.AUDIT,
                    priority: NotificationPriority.HIGH,
                });
            }
        );

        // Handle renewPlan
        builder.addCase(renewPlan.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(renewPlan.fulfilled, (state, action: PayloadAction<any>) => {
            state.isLoading = false;
            if (state.selectedOrganization && state.selectedOrganization._id === action.payload.orgId) {
                state.selectedOrganization = {
                    ...state.selectedOrganization,
                    subscriptionPlan: action.payload.organization.subscriptionPlan,
                };
            }
        });
        builder.addCase(renewPlan.rejected, (state, action) => {
            state.isLoading = false;
            state.error = (action.payload as string) || "An error occurred";
            minimalToast({
                title: "Error",
                message: state.error,
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.HIGH,
            });
        });

        // Handle deleteOrganization
        builder.addCase(deleteOrganization.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(deleteOrganization.fulfilled, (state, action: PayloadAction<{ organizationId: string; data: any }>) => {
            state.isLoading = false;
            const { organizationId } = action.payload;
            // Remove the deleted organization from the arrays
            state.allOrganizations = state.allOrganizations.filter(org => org._id !== organizationId);
            state.organizationsOfAdmin = state.organizationsOfAdmin.filter(org => org._id !== organizationId);
            // Clear selected organization if it was deleted
            if (state.selectedOrganization && state.selectedOrganization._id === organizationId) {
                state.selectedOrganization = null;
            }
        });
        builder.addCase(deleteOrganization.rejected, (state, action) => {
            state.isLoading = false;
            state.error = (action.payload as string) || "An error occurred";
            minimalToast({
                title: "Error",
                message: state.error,
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.ALERT,
                priority: NotificationPriority.HIGH,
            });
        });
    },
});

export const {
    setSelectedOrganization,
    setSelectedAuditCategory,
    updateAuditCategoryLists,
    updateAICredits,
    updateOrganizationDetails,
    updateOrganizationCustomization,
    clearSubOrganizations,
} = organizationsSlice.actions;
export const organizationReducer = organizationsSlice.reducer;

/** Base org list for the current role (masters + standalone, no lazy-loaded subs). */
export const selectBaseOrganizations = (state: RootState): Organization[] => {
    const { auth, organization } = state;
    if (auth.p3mSuperAdminMode) return organization.allOrganizations;
    if (auth.adminMode || auth.ownerMode || auth.superAdminMode) {
        return organization.organizationsOfAdmin;
    }
    return (auth.user?.organization as Organization[] | undefined) || [];
};

/**
 * Navbar / picker list: base orgs plus sub-orgs for the loaded master.
 * Memoized so the array reference stays stable across unrelated store updates —
 * a fresh array on every render would re-run the Navbar org-restore effect and,
 * combined with a saved-org id that isn't in the list, spin into an infinite
 * fetchOrganizationById loop ("Maximum update depth exceeded").
 */
export const selectOrgPickerList = createSelector(
    [
        selectBaseOrganizations,
        (state: RootState) => state.organization.subOrganizations,
    ],
    (baseOrgs, subOrgs): Organization[] =>
        mergeOrganizationsWithSubOrgs(baseOrgs, subOrgs)
);

export { getMasterOrgIdForChildren, mergeOrganizationsWithSubOrgs };
